import { motion } from 'framer-motion';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.1 },
  transition: { duration: 0.5 },
};

const sections = [
  {
    number: '1',
    title: 'Data Controller',
    content: (
      <>
        <p>
          CrossPay Solutions SAS, identified with Tax ID (NIT) 900.648.294-5 and headquartered
          in Bogot&aacute; D.C., will act as the data controller for the personal data collected
          in the course of its operations.
        </p>
        <p className="mt-3">
          Contact email:{' '}
          <a href="mailto:legal@crosspaysolutions.com.co" className="text-accent-magenta hover:underline">
            legal@crosspaysolutions.com.co
          </a>
        </p>
      </>
    ),
  },
  {
    number: '2',
    title: 'Purposes of Data Processing',
    content: (
      <>
        <p>The personal data collected will be used for the following purposes:</p>
        <ul className="list-disc list-inside mt-3 space-y-2">
          <li>Execute the commercial and contractual relationship with clients, suppliers, and partners.</li>
          <li>Verify user identity and carry out Know Your Customer (KYC/KYB) procedures either directly or through third parties.</li>
          <li>Comply with legal, contractual, tax, and regulatory obligations.</li>
          <li>Offer financial products and services, including notifications about updates or changes.</li>
          <li>Assess risks and prevent unlawful activities, including money laundering and terrorist financing.</li>
          <li>Handle requests, complaints, or claims.</li>
        </ul>
      </>
    ),
  },
  {
    number: '3',
    title: 'Data Collected',
    content: (
      <>
        <p>
          Depending on the relationship with the data subject, we may collect the following types
          of information:
        </p>
        <ul className="list-disc list-inside mt-3 space-y-2">
          <li>Identification data (name, ID number, nationality, etc.)</li>
          <li>Contact information (email, phone number, address)</li>
          <li>Financial and tax information</li>
          <li>Verification documents (ID card, tax registration, certificate of incorporation, etc.)</li>
          <li>Transactional information</li>
        </ul>
      </>
    ),
  },
  {
    number: '4',
    title: 'Data Transfer and Transmission',
    content: (
      <p>
        The data may be transferred or transmitted to partners, technology providers, or competent
        authorities, whether national or international, solely for the purpose of fulfilling the
        service and the purposes described herein and in accordance with the law.
      </p>
    ),
  },
  {
    number: '5',
    title: 'Rights of the Data Subject',
    content: (
      <>
        <p>
          In accordance with Law 1581 of 2012 and Decree 1377 of 2013, the data subject has
          the right to:
        </p>
        <ul className="list-disc list-inside mt-3 space-y-2">
          <li>Access, update, and correct their personal data.</li>
          <li>Request proof of the authorization granted.</li>
          <li>Be informed about how their data has been used.</li>
          <li>File complaints before the Superintendence of Industry and Commerce (SIC).</li>
          <li>Revoke authorization and/or request the deletion of their data when legal principles are not respected.</li>
        </ul>
      </>
    ),
  },
  {
    number: '6',
    title: 'Procedure for Exercising Rights',
    content: (
      <p>
        Data subjects may exercise their rights by submitting a request via email to{' '}
        <a href="mailto:legal@crosspaysolutions.com.co" className="text-accent-magenta hover:underline">
          legal@crosspaysolutions.com.co
        </a>
        , clearly indicating the right they wish to exercise and attaching a copy of their
        identification document.
      </p>
    ),
  },
  {
    number: '7',
    title: 'Validity',
    content: (
      <p>
        This policy is effective as of April 4, 2026 and may be amended at any time, subject to
        publication through our official channels.
      </p>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <main id="main-content">
      {/* Hero */}
      <section className="pt-40 pb-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center_top,rgba(168,67,151,0.08),transparent_70%)] pointer-events-none" />
        <div className="mx-auto min-w-0 max-w-4xl px-4 sm:px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black font-headline tracking-tight">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            <p className="text-on-surface-variant text-lg mt-4">
              CrossPay Solutions SAS
            </p>
            <p className="text-on-surface-variant/70 mt-2 text-sm">
              Effective Date: April 4, 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content sections */}
      <section className="pb-20">
        <div className="mx-auto min-w-0 max-w-3xl space-y-10 px-4 sm:px-6">
          {sections.map((s, i) => (
            <motion.div
              key={s.number}
              className="p-6 border border-outline-variant/30 rounded-xl hover:border-purple-primary/50 transition-all duration-300"
              {...fadeIn}
              transition={{ delay: i * 0.06, duration: 0.5 }}
            >
              <h2 className="text-xl font-bold font-headline mb-3">
                <span className="gradient-text">{s.number}.</span> {s.title}
              </h2>
              <div className="text-sm text-on-surface-variant leading-relaxed">
                {s.content}
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </main>
  );
}
